/**
 * Chart theme tokens for the Common Dashboard Framework.
 *
 * Palette is the validated dataviz reference instance (see dataviz skill,
 * references/palette.md). Categorical hues are assigned in FIXED ORDER, never
 * cycled — the order is the CVD-safety mechanism. Both light and dark columns
 * pass the validator's adjacent-pair gates; the light column carries a
 * sub-3:1 contrast WARN on aqua/yellow/magenta, which we relieve with an
 * always-present legend + tooltip + CSV/table view (never color alone).
 *
 * All-pairs forms (scatter/bubble) must cap at the first 3 slots — past that
 * the palette can't clear the all-pairs floor. ChartWidget enforces this.
 */
import { useEffect, useState } from 'react';

const SERIES_LIGHT = [
  '#2a78d6', '#eb6834', '#1baf7a', '#eda100',
  '#e87ba4', '#008300', '#4a3aa7', '#e34948',
];
const SERIES_DARK = [
  '#3987e5', '#d95926', '#199e70', '#c98500',
  '#d55181', '#008300', '#9085e9', '#e66767',
];

const CHROME_LIGHT = {
  surface: '#fcfcfb', primaryInk: '#0b0b0b', secondaryInk: '#52514e',
  muted: '#898781', grid: '#e1e0d9', axis: '#c3c2b7', other: '#898781',
};
const CHROME_DARK = {
  surface: '#1a1a19', primaryInk: '#ffffff', secondaryInk: '#c3c2b7',
  muted: '#898781', grid: '#2c2c2a', axis: '#383835', other: '#898781',
};

export function getChartPalette(isDark) {
  const series = isDark ? SERIES_DARK : SERIES_LIGHT;
  const chrome = isDark ? CHROME_DARK : CHROME_LIGHT;
  return { ...chrome, series, seriesCount: series.length };
}

/**
 * Resolve a series color by fixed index. Slots beyond the 8-hue palette fold
 * to the muted "Other" ink rather than cycling (a 9th hue is never generated).
 */
export function seriesColor(palette, index) {
  return index < palette.series.length ? palette.series[index] : palette.other;
}

/** Detects the viewer's theme (OS preference + explicit data-theme override). */
export function useChartTheme() {
  const [isDark, setIsDark] = useState(() => detectDark());
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    const update = () => setIsDark(detectDark());
    mq?.addEventListener?.('change', update);
    const obs = new MutationObserver(update);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => {
      mq?.removeEventListener?.('change', update);
      obs.disconnect();
    };
  }, []);
  return { isDark, palette: getChartPalette(isDark) };
}

function detectDark() {
  if (typeof document === 'undefined') return false;
  const explicit = document.documentElement.getAttribute('data-theme');
  if (explicit === 'dark') return true;
  if (explicit === 'light') return false;
  return !!window.matchMedia?.('(prefers-color-scheme: dark)')?.matches;
}
